import { computed, type ComputedRef } from 'vue'

import { useIntegrations } from '@/features/integrations'

export interface DefinitionPlatformDescriptor {
  labelKey: string
  field: string
  channel: string
  /** Module-12 registry keys that authorize this descriptor's existence. */
  providerKeys: readonly string[]
}

const SOURCE_MEDIUM_DESCRIPTORS = [
  ['metaAds', 'meta_ads', 'meta', 'meta_ads_account'],
  ['googleAds', 'google_ads', 'google', 'google_ads_account'],
  ['tiktokAds', 'tiktok_ads', 'tiktok', 'tiktok_ads_account'],
  ['snapchat', 'snapchat_Ads', 'snapchat', 'snapchat_account'],
  ['criteo', 'criteo', 'criteo', 'criteo_account'],
  ['rtbHouse', 'rtb_house', 'rtb', 'rtb_account'],
  ['bingAds', 'bing_ads', 'bing', 'bing_ads_account'],
  ['linkedinAds', 'linkedin', 'linkedin', 'linkedin_ads_account'],
  ['reklamStore', 'reklamstore', 'reklam_store', 'reklamstore_account'],
  ['pinterest', 'pinterest', 'pinterest', 'pinterest_account'],
  ['dv360', 'dv360', 'dv360', 'dv360_advertiser'],
  ['gelirOrtaklari', 'gelirortaklari', 'gelir', 'gelirortaklari_account'],
  ['yandexAds', 'yandex_ads', 'yandex', 'yandex_direct_account'],
  ['twitterAds', 'twitter_ads', 'twitter', 'twitter_ad_account'],
  ['appleSearchAds', 'apple_search_ads', 'apple_search_ads', 'apple_search_ads_account'],
  ['yango', 'yango', 'yango', 'yango_account'],
] as const

export const sourceMediumPlatformDescriptors: readonly DefinitionPlatformDescriptor[] =
  SOURCE_MEDIUM_DESCRIPTORS.map(([name, field, channel, providerKey]) => ({
    labelKey: `workspaceDefinitions.platform.${name}`,
    field,
    channel,
    providerKeys: [providerKey],
  }))

export const productMappingPlatformDescriptors: readonly DefinitionPlatformDescriptor[] = [
  {
    labelKey: 'workspaceDefinitions.platform.merchantCenter',
    field: 'merchant_center',
    channel: 'merchant_center',
    providerKeys: ['merchant_center_account'],
  },
  {
    labelKey: 'workspaceDefinitions.platform.metaCatalog',
    field: 'meta_catalog',
    channel: 'meta_catalog',
    // Meta catalog is a sub-entity configured from the Meta provider rather than a connectable
    // provider of its own. The parent key keeps this descriptor registry-derived.
    providerKeys: ['meta_ads_account'],
  },
]

function activeDescriptors(
  descriptors: readonly DefinitionPlatformDescriptor[],
): ComputedRef<readonly DefinitionPlatformDescriptor[]> {
  const integrations = useIntegrations()
  return computed(() => {
    const registryKeys = new Set(integrations.providers.value.map((provider) => provider.key))
    return descriptors.filter(
      (descriptor) =>
        descriptor.providerKeys.some((key) => registryKeys.has(key)) &&
        integrations.isConnected(descriptor.channel),
    )
  })
}

/** Reactive replacement for v3's one-time `ref([...].filter(active))` snapshot. */
export function useSourceMediumPlatforms(): ComputedRef<readonly DefinitionPlatformDescriptor[]> {
  return activeDescriptors(sourceMediumPlatformDescriptors)
}

/** Reactive product-feed mapping descriptors, including Meta Catalog's sub-entity channel. */
export function useProductMappingPlatforms(): ComputedRef<readonly DefinitionPlatformDescriptor[]> {
  return activeDescriptors(productMappingPlatformDescriptors)
}
